// Date localization utilities for multi-language support

export interface MonthMapping {
  [genitiveForm: string]: string; // genitive -> nominative
}

export interface LanguageConfig {
  code: string;
  name: string;
  monthMappings: MonthMapping;
}

// Language configurations
export const LANGUAGE_CONFIGS: { [languageCode: string]: LanguageConfig } = {
  pl: {
    code: 'pl',
    name: 'Polish',
    monthMappings: {
      'stycznia': 'styczeń',
      'lutego': 'luty',
      'marca': 'marzec',
      'kwietnia': 'kwiecień',
      'maja': 'maj',
      'czerwca': 'czerwiec',
      'lipca': 'lipiec',
      'sierpnia': 'sierpień',
      'września': 'wrzesień',
      'października': 'październik',
      'listopada': 'listopad',
      'grudnia': 'grudzień'
    }
  }
  // Future languages can be added here, for example:
  // en: {
  //   code: 'en',
  //   name: 'English',
  //   monthMappings: {} // English doesn't need genitive->nominative conversion
  // },
  // de: {
  //   code: 'de',
  //   name: 'German',
  //   monthMappings: {
  //     // German month mappings if needed
  //   }
  // }
};

/**
 * Applies language-specific month name transformations to formatted date strings
 * @param formatted - The formatted date string from date-fns
 * @param languageCode - The language code (e.g., 'pl', 'en', 'de')
 * @returns The transformed date string with proper month names
 */
export const applyMonthTransformations = (formatted: string, languageCode: string): string => {
  const config = LANGUAGE_CONFIGS[languageCode];
  
  if (!config || !config.monthMappings) {
    return formatted; // Return original if no config or mappings found
  }

  let result = formatted;
  Object.entries(config.monthMappings).forEach(([sourceForm, targetForm]) => {
    result = result.replace(new RegExp(sourceForm, 'gi'), targetForm);
  });

  return result;
};

/**
 * Gets the default language code for the application
 * This can be extended to read from user preferences, browser settings, etc.
 */
export const getDefaultLanguageCode = (): string => {
  // For now, default to Polish since that's what the app currently uses
  // In the future, this could read from:
  // - User preferences
  // - Browser language settings
  // - Application configuration
  return 'pl';
};

/**
 * Gets available language codes
 */
export const getAvailableLanguages = (): string[] => {
  return Object.keys(LANGUAGE_CONFIGS);
};

/**
 * Checks if a language is supported
 */
export const isLanguageSupported = (languageCode: string): boolean => {
  return languageCode in LANGUAGE_CONFIGS;
};